import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";

import "./fmdo.scss";
import {
  ASSETS, WHATSAPP_URL, STATS, WHY_CARDS, DSP_ROW_1, DSP_ROW_2, FAQS,
  OFFER_START, OFFER_END, PLAN_SLUGS,
} from "./data";
import { loadMetaPixel } from "./pixel";
import Testimonials from "./Testimonials";
import OfferPopup from "./OfferPopup";
import ApplicationModal from "./ApplicationModal";

gsap.registerPlugin(ScrollTrigger);

function formatCountdown(diff) {
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return `${String(d).padStart(2, "0")}d : ${String(h).padStart(2, "0")}h : ${String(m).padStart(2, "0")}m`;
}

function getCountdown() {
  const start = new Date(OFFER_START);
  const end = new Date(OFFER_END);
  const now = new Date();
  if (now < start) return { label: "Offer starts in", time: formatCountdown(start - now) };
  if (now <= end) return { label: "Offer ends in", time: formatCountdown(end - now) };
  return { label: "", time: "Offer expired" };
}

const WhatsAppIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="28" height="28" fill="none">
    <path fill="#fff" d="M24 4C13 4 4 13 4 24c0 3.6 1 7 2.7 10L4 44l10.3-2.7C17.1 43 20.5 44 24 44c11 0 20-9 20-20S35 4 24 4z" />
    <path fill="#25D366" d="M24 6C14.1 6 6 14.1 6 24c0 3.4.9 6.6 2.5 9.4L6.2 41.8l8.6-2.3C17.4 41.1 20.6 42 24 42c9.9 0 18-8.1 18-18S33.9 6 24 6z" />
    <path fill="#fff" d="M33.5 28.8c-.5-.2-2.9-1.4-3.3-1.6-.4-.2-.7-.2-1 .2-.3.5-1.1 1.4-1.4 1.7-.3.3-.5.3-1 .1-.5-.2-2-.7-3.8-2.3-1.4-1.2-2.3-2.8-2.6-3.2-.3-.5 0-.7.2-1 .2-.2.5-.5.7-.8.2-.3.3-.5.4-.8.1-.3 0-.6-.1-.8-.1-.2-1-2.5-1.4-3.4-.4-.9-.8-.8-1-.8h-.9c-.3 0-.8.1-1.2.6-.4.5-1.6 1.5-1.6 3.7s1.6 4.3 1.8 4.6c.2.3 3.1 4.9 7.6 6.7 1.1.5 1.9.7 2.6.9 1.1.3 2.1.3 2.9.2.9-.1 2.9-1.2 3.3-2.3.4-1.1.4-2.1.3-2.3-.1-.3-.4-.4-.9-.6z" />
  </svg>
);

const DspChip = ({ img, name }) => (
  <div className="dsp-chip"><img src={img} alt={name} /><span>{name}</span></div>
);

export default function AdsPage() {
  const navigate = useNavigate();
  const pageRef = useRef(null);
  const lenisRef = useRef(null);
  const faqRefs = useRef([]);

  const [menuOpen, setMenuOpen] = useState(false);
  const [countdown, setCountdown] = useState(getCountdown);
  const [openFaq, setOpenFaq] = useState(-1);
  const [modalPlan, setModalPlan] = useState(null); // null | 'starter' | 'artist_pro' | 'label'

  /* ── Page chrome: body classes + Meta Pixel ── */
  useEffect(() => {
    document.body.classList.add("fmdo-body");
    document.documentElement.classList.add("fmdo-html");
    loadMetaPixel();
    return () => {
      document.body.classList.remove("fmdo-body");
      document.documentElement.classList.remove("fmdo-html");
      document.body.style.overflow = "";
    };
  }, []);

  /* ── Lenis + GSAP animations (ported from the original inline script) ── */
  useEffect(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let lenis = null;
    let tickerFn = null;

    if (!reduceMotion) {
      lenis = new Lenis({ duration: 1.15, smoothWheel: true, smoothTouch: false });
      lenisRef.current = lenis;
      lenis.on("scroll", ScrollTrigger.update);
      tickerFn = (t) => lenis.raf(t * 1000);
      gsap.ticker.add(tickerFn);
      gsap.ticker.lagSmoothing(0);
    }

    const onMove = (e) => {
      gsap.to("#cursor-glow", { x: e.clientX, y: e.clientY, duration: 0.5, ease: "power2.out" });
      gsap.to("#cursor-dot", { x: e.clientX, y: e.clientY, duration: 0.08, ease: "power2.out" });
      gsap.to("#cursor-ring", { x: e.clientX, y: e.clientY, duration: 0.28, ease: "power2.out" });
    };
    const onLeave = () => {
      gsap.to("#cursor-glow", { opacity: 0, duration: 0.3 });
      gsap.to(["#cursor-dot", "#cursor-ring"], { opacity: 0, duration: 0.2 });
    };
    const onEnter = () => {
      gsap.to("#cursor-glow", { opacity: 1, duration: 0.3 });
      gsap.to(["#cursor-dot", "#cursor-ring"], { opacity: 1, duration: 0.2 });
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);

    const ctx = gsap.context(() => {
      // Navbar scrolled state
      ScrollTrigger.create({
        start: "top -60",
        onEnter: () => document.getElementById("fmdo-navbar")?.classList.add("scrolled"),
        onLeaveBack: () => document.getElementById("fmdo-navbar")?.classList.remove("scrolled"),
      });

      // Hero entrance
      gsap.from("#fmdo-navbar", { y: -60, opacity: 0, duration: 0.8, ease: "power3.out", delay: 0.2 });
      gsap.timeline({ delay: 0.45 })
        .to(".hero-badge", { opacity: 1, y: 0, duration: 0.6, ease: "power3.out" })
        .from(".hero-h1", { opacity: 0, y: 30, duration: 0.9, ease: "power3.out" }, "-=0.2")
        .to(".hero-sub", { opacity: 1, y: 0, duration: 0.7, ease: "power3.out" }, "-=0.4")
        .to(".hero-btns", { opacity: 1, y: 0, duration: 0.7, ease: "power3.out" }, "-=0.4")
        .to(".hero-glow-line", { opacity: 1, duration: 1.2, ease: "power2.out" }, "-=0.5");

      // Hero scroll-away parallax
      if (!reduceMotion) {
        const heroParallaxY = window.innerWidth > 768 ? 120 : 60;
        gsap.to(".hero-inner", {
          scrollTrigger: { trigger: "#hero", start: "top top", end: "bottom top", scrub: true },
          y: heroParallaxY, opacity: 0.2, scale: 0.96, ease: "none",
        });
      }

      // Marquee
      gsap.to("#marquee-row-1 .marquee-inner", { x: "-50%", duration: 38, ease: "none", repeat: -1 });
      gsap.fromTo("#marquee-row-2 .marquee-inner", { x: "-50%" }, { x: "0%", duration: 30, ease: "none", repeat: -1 });
      gsap.from("#platforms", {
        scrollTrigger: { trigger: "#platforms", start: "top 85%", toggleActions: "play none none none" },
        y: 50, opacity: 0, duration: 0.9, ease: "power3.out",
      });

      // Generic reveal-on-scroll
      gsap.utils.toArray(".reveal").forEach((el) => {
        gsap.to(el, {
          scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none none" },
          opacity: 1, y: 0, duration: 0.7, ease: "power3.out",
        });
      });

      // Stagger groups
      const staggerGroup = (sel, trigger) => {
        gsap.to(sel, {
          scrollTrigger: { trigger, start: "top 82%", toggleActions: "play none none none" },
          opacity: 1, y: 0, duration: 0.6, stagger: 0.1, ease: "power3.out",
        });
      };
      staggerGroup(".why-grid .why-card", ".why-grid");
      staggerGroup(".pricing-grid .price-card", ".pricing-grid");
      staggerGroup(".faq-list .faq-item", ".faq-list");

      // Scrub parallax on section headings
      if (!reduceMotion && window.innerWidth > 768) {
        gsap.utils.toArray(".section-title").forEach((title) => {
          gsap.fromTo(title, { y: 30 }, {
            y: -30, ease: "none",
            scrollTrigger: { trigger: title, start: "top bottom", end: "bottom top", scrub: true },
          });
        });
      }

      // Stats count-up
      gsap.utils.toArray(".stat-num").forEach((el) => {
        const target = parseFloat(el.getAttribute("data-count"));
        const suffix = el.getAttribute("data-suffix") || "";
        const obj = { val: 0 };
        ScrollTrigger.create({
          trigger: el, start: "top 90%", once: true,
          onEnter: () => gsap.to(obj, {
            val: target, duration: 1.6, ease: "power2.out",
            onUpdate: () => {
              const v = obj.val;
              const isDecimal = target % 1 !== 0;
              el.textContent = (isDecimal ? v.toFixed(1) : (v >= 1000 ? Math.round(v).toLocaleString() : Math.round(v))) + suffix;
            },
          }),
        });
      });

      // CTA
      gsap.to("#cta-sweep", { scrollTrigger: { trigger: "#cta", start: "top 80%", toggleActions: "play none none none" }, width: "100%", duration: 1.0, ease: "power3.inOut" });
      gsap.to("#cta-heading", { scrollTrigger: { trigger: "#cta", start: "top 75%", toggleActions: "play none none none" }, scale: 1, opacity: 1, duration: 0.9, ease: "power3.out", delay: 0.1 });
      gsap.to(".cta-sub", { scrollTrigger: { trigger: "#cta", start: "top 72%", toggleActions: "play none none none" }, opacity: 1, y: 0, duration: 0.7, ease: "power3.out", delay: 0.3 });
      gsap.to("#cta-btn", { scrollTrigger: { trigger: "#cta", start: "top 70%", toggleActions: "play none none none" }, opacity: 1, y: 0, duration: 0.7, ease: "back.out(1.5)", delay: 0.45 });
      gsap.to("#cta-btn", { boxShadow: "0 0 30px rgba(74,222,128,0.5)", duration: 1.4, ease: "power1.inOut", repeat: -1, yoyo: true, delay: 2 });
    }, pageRef);

    return () => {
      ctx.revert();
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      if (tickerFn) gsap.ticker.remove(tickerFn);
      if (lenis) lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  /* ── Keep the navbar below the sale bar, whatever height it renders at ── */
  useEffect(() => {
    const pageEl = pageRef.current;
    const saleBar = pageEl?.querySelector("#sale-bar");
    if (!pageEl || !saleBar) return;
    const setOffset = () =>
      pageEl.style.setProperty("--fmdo-salebar-h", `${saleBar.offsetHeight}px`);
    setOffset();
    const ro = new ResizeObserver(setOffset);
    ro.observe(saleBar);
    window.addEventListener("resize", setOffset);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", setOffset);
    };
  }, []);

  /* ── Starter countdown ── */
  useEffect(() => {
    const t = setInterval(() => setCountdown(getCountdown()), 60000);
    return () => clearInterval(t);
  }, []);

  /* ── Video restart when tab becomes visible again ── */
  useEffect(() => {
    const onVis = () => {
      const v = document.getElementById("hero-video");
      if (!document.hidden && v && v.paused) v.play().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  /* ── FAQ accordion height (mirrors original maxHeight animation) ── */
  useEffect(() => {
    faqRefs.current.forEach((el, i) => {
      if (!el) return;
      el.style.maxHeight = i === openFaq ? `${el.scrollHeight}px` : null;
    });
    ScrollTrigger.refresh();
  }, [openFaq]);

  const scrollToSel = useCallback((sel, offset = -70) => {
    const target = typeof sel === "string" ? pageRef.current?.querySelector(sel) : sel;
    if (!target) return;
    if (lenisRef.current) lenisRef.current.scrollTo(target, { offset });
    else target.scrollIntoView({ behavior: "smooth" });
  }, []);

  const anchor = (sel) => (e) => { e.preventDefault(); scrollToSel(sel); };
  const mobileAnchor = (sel) => (e) => {
    e.preventDefault();
    setMenuOpen(false);
    document.body.style.overflow = "";
    setTimeout(() => scrollToSel(sel), 320);
  };

  const toggleMenu = () => {
    setMenuOpen((open) => {
      document.body.style.overflow = !open ? "hidden" : "";
      return !open;
    });
  };

  const openPlanModal = (plan) => (e) => {
    e.preventDefault();
    setModalPlan(plan);
    document.body.style.overflow = "hidden";
    lenisRef.current?.stop();
  };
  const closeModal = () => {
    setModalPlan(null);
    document.body.style.overflow = "";
    lenisRef.current?.start();
  };
  const goToCheckout = (applicationId) => {
    document.body.style.overflow = "";
    navigate(`/ads/plan/${PLAN_SLUGS[modalPlan]}?app=${encodeURIComponent(applicationId)}`);
  };

  return (
    <div className="fmdo-page" ref={pageRef}>
      <div id="cursor-glow" />
      <div id="cursor-dot" />
      <div id="cursor-ring" />

      {/* Sale bar */}
      <div id="sale-bar">
        <span className="sale-pulse">🔥</span> <strong>Annual Special Offer</strong>
        <span className="sale-full"> — Live July to September &nbsp;·&nbsp; Lock in your release price now</span>
        <span className="sale-mob"> — Lock in now</span> &nbsp;<span className="sale-pulse">🔥</span>
      </div>

      {/* Navbar */}
      <nav id="fmdo-navbar">
        <div className="nav-inner">
          <a className="nav-logo" href="#hero" onClick={anchor("#hero")}>
            <img src={ASSETS.logoWhite} alt="FM Digital Official" />
          </a>
          <div className="nav-links">
            <a href="#why" onClick={anchor("#why")}>Services</a>
            <a href="#pricing" onClick={anchor("#pricing")}>Pricing</a>
            <a href="#faq" onClick={anchor("#faq")}>Support</a>
            <a href="#cta" onClick={anchor("#cta")} className="nav-cta">Start Releasing Today →</a>
          </div>
          <button className={`nav-burger${menuOpen ? " open" : ""}`} aria-label="Open menu"
            aria-expanded={menuOpen} onClick={toggleMenu}>
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* Mobile Nav Overlay */}
      <div id="mobile-menu" className={menuOpen ? "open" : ""} aria-hidden={!menuOpen}>
        <a href="#why" onClick={mobileAnchor("#why")}>Services</a>
        <a href="#pricing" onClick={mobileAnchor("#pricing")}>Pricing</a>
        <a href="#faq" onClick={mobileAnchor("#faq")}>Support</a>
        <a href="#cta" onClick={mobileAnchor("#cta")} className="mobile-cta">Start Releasing Today →</a>
      </div>

      {/* Hero */}
      <section id="hero">
        <div className="hero-video-wrap" aria-hidden="true">
          <video id="hero-video" autoPlay muted loop playsInline
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback"
            onContextMenu={(e) => e.preventDefault()}>
            <source src={ASSETS.heroVideo} type="video/mp4" />
          </video>
          <div className="hero-video-overlay" />
        </div>
        <div className="hero-inner">
          <div className="hero-badge">🎵 #InkIngIt</div>
          <h1 className="hero-h1">
            <span className="q">“</span>where every <em>stream counts</em><br />and every <em>artist matters</em><span className="q">”</span>
          </h1>
          <p className="hero-sub">Distribute to 190+ platforms while keeping control of your music and earnings.</p>
          <div className="hero-btns">
            <a href="#pricing" onClick={anchor("#pricing")} className="btn-primary">Start Releasing Today</a>
          </div>
        </div>
        <div className="hero-glow-line" />
      </section>

      {/* Pricing */}
      <section id="pricing">
        <div className="section-inner">
          <span className="eyebrow reveal">Pricing</span>
          <h2 className="section-title reveal">Plans That <em>Scale With You</em></h2>
          <p className="section-lead reveal">No upfront fees. Choose the release plan that fits where you are right now.</p>
          <div className="pricing-grid">

            <div className="price-card featured reveal">
              <div className="price-off-ribbon">20% OFF</div>
              <span className="price-badge">For Artists</span>
              <div className="price-name">Artist Pro</div>
              <div className="price-original-row">
                <span className="price-original">₹2,999</span>
                <span className="price-sale-tag">Limited Offer</span>
              </div>
              <div className="price-amt"><sup>₹</sup>2,399<span> / year</span></div>
              <div className="price-gst">incl. GST</div>
              <div className="price-note">Unlimited releases, zero hidden fees</div>
              <hr className="price-divider" />
              <ul className="price-features">
                <li>Unlimited releases</li>
                <li>190+ DSPs &amp; stores</li>
                <li>Advanced metadata tools</li>
                <li>Monthly royalty report</li>
                <li>Free UPC &amp; ISRC code</li>
                <li>Priority delivery</li>
                <li>Revenue: 100% to Artist</li>
                <li>Publishing rights &amp; revenue</li>
                <li>24/7 Support panel</li>
                <li>Referral earnings</li>
              </ul>
              <a href="#apply" className="price-btn solid" onClick={openPlanModal("artist_pro")}>Get Started</a>
            </div>

            <div className="price-card starter reveal">
              <div className="july-ribbon">✦ ONLY FOR AUGUST ✦</div>
              <div className="starter-popular-row">
                <span className="starter-green-dot" />
                <span className="price-badge">Most Popular</span>
              </div>
              <div className="price-name">Starter</div>
              <div className="price-note">Annual special offer — lock in your price now</div>
              <div className="price-original-row">
                <span className="price-original">₹899</span>
                <span className="starter-save-pill">Save ₹360</span>
              </div>
              <div className="price-amt"><sup>₹</sup>539<span> / release</span></div>
              <div className="price-gst">incl. GST</div>
              <div className="starter-monthly">That's just ₹45/month billed annually</div>
              <div className="starter-countdown">
                <span>⏱</span>
                <span>{countdown.label}</span>
                <span className="countdown-time">{countdown.time}</span>
              </div>
              <hr className="price-divider" />
              <ul className="price-features">
                <li>1 song distribution</li>
                <li>190+ DSPs &amp; stores</li>
                <li>Basic metadata tools</li>
                <li>Free UPC &amp; ISRC code</li>
                <li>Standard delivery time</li>
                <li>Revenue: 70% / 30%</li>
                <li>Publishing rights &amp; revenue</li>
                <li>24/7 Support panel</li>
              </ul>
              <a href="#apply" className="price-btn starter-claim-btn" onClick={openPlanModal("starter")}>Claim 40% off →</a>
              <div className="starter-trust"><span>🔒 Secure payment</span></div>
            </div>

            <div className="price-card label-card reveal">
              <span className="price-badge">For Labels</span>
              <div className="price-name">Label</div>
              <div className="price-original-row" style={{ visibility: "hidden" }}>
                <span className="price-original">—</span>
              </div>
              <div className="price-amt custom">Custom</div>
              <div className="price-note">Tailored to your roster &amp; volume</div>
              <hr className="price-divider" />
              <ul className="price-features">
                <li>Everything in Artist Pro</li>
                <li>Manage multiple artists &amp; labels</li>
                <li>Bulk release tools</li>
                <li>Unlock new revenue streams</li>
                <li>Dedicated account manager</li>
                <li>Custom royalty splits</li>
                <li>White-label dashboard</li>
              </ul>
              <a href="#apply" className="price-btn ghost" onClick={openPlanModal("label")}>Contact Us</a>
            </div>

          </div>
        </div>
      </section>

      {/* Stats */}
      <section id="stats">
        <div className="stats-grid">
          {STATS.map((s) => (
            <div className="stat-item reveal" key={s.label}>
              <div className="stat-num" data-count={s.count} data-suffix={s.suffix}>0</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Why FM Digital Official */}
      <section id="why">
        <div className="section-inner">
          <span className="eyebrow reveal">Why FM Digital Official</span>
          <h2 className="section-title reveal">Everything You Need to <em>Release &amp; Grow</em></h2>
          <p className="section-lead reveal">One platform built to get your music out, protect it, and pay you — wherever your fans are.</p>
          <div className="why-grid">
            {WHY_CARDS.map((c) => (
              <div className="why-card reveal" key={c.title}>
                <div className="why-icon">{c.icon}</div>
                <div className="why-title">{c.title}</div>
                <div className="why-desc">{c.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms */}
      <section id="platforms">
        <p className="platforms-label">DSPs &amp; Stores</p>
        <p className="platforms-sub">More than 190+ platforms worldwide</p>
        <div className="marquee-container">
          <div className="marquee-wrapper">
            <div className="marquee-row" id="marquee-row-1">
              <div className="marquee-inner">
                {[...DSP_ROW_1, ...DSP_ROW_1].map((d, i) => <DspChip key={i} {...d} />)}
              </div>
            </div>
            <div className="marquee-row" id="marquee-row-2">
              <div className="marquee-inner">
                {[...DSP_ROW_2, ...DSP_ROW_2].map((d, i) => <DspChip key={i} {...d} />)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* FAQ */}
      <section id="faq">
        <div className="section-inner">
          <span className="eyebrow reveal">Support</span>
          <h2 className="section-title reveal">Frequently Asked <em>Questions</em></h2>
          <div className="faq-list">
            {FAQS.map((f, i) => (
              <div className={`faq-item reveal${openFaq === i ? " open" : ""}`} key={i}>
                <button className="faq-q" onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                  {f.q}<span className="faq-icon">+</span>
                </button>
                <div className="faq-a" ref={(el) => { faqRefs.current[i] = el; }}>
                  <div className="faq-a-inner">
                    {f.a}
                    {f.list && <ul>{f.list.map((li, j) => <li key={j}>{li}</li>)}</ul>}
                    {f.after && <>{" "}{f.after}</>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="cta">
        <div className="cta-sweep-line" id="cta-sweep" />
        <div className="cta-inner">
          <h2 className="cta-h2" id="cta-heading">Release Your Music <em>Worldwide</em></h2>
          <p className="cta-sub">Distribute to 190+ platforms while keeping control of your music and earnings.</p>
          <a href="#pricing" className="btn-primary btn-large" id="cta-btn" onClick={anchor("#pricing")}>Start Releasing Today →</a>
        </div>
      </section>

      {/* Footer */}
      <footer id="footer">
        <div className="footer-top">
          <div className="footer-brand">
            <img src={ASSETS.logoWhite} alt="FM Digital Official" />
            <p>Release your music worldwide while keeping control of your music and earnings.</p>
          </div>
          <div className="footer-col">
            <h4>Artist &amp; Label Services</h4>
            <a href="/sell-your-music">Sell Your Music</a>
            <a href="/services/featured-playlists">Playlisting</a>
            <a href="/services/music-marketing">Marketing</a>
            <a href="#pricing" onClick={anchor("#pricing")}>Pricing</a>
          </div>
          <div className="footer-col">
            <h4>About</h4>
            <a href="/about?section=1">Our Story</a>
            <a href="/about?section=2">Our Mission</a>
            <a href="/about?section=3">Why Choose Us?</a>
            <a href="/about?section=4">Contact</a>
          </div>
          <div className="footer-col">
            <h4>Legal</h4>
            <a href="/support/legal-documents?section=0">Terms of service</a>
            <a href="/support/legal-documents?section=1">Distro Agreement</a>
            <a href="/support/legal-documents?section=2">Analytics Disclosure</a>
            <a href="/support/legal-documents?section=3">Privacy Policy</a>
            <a href="/support/legal-documents?section=4">Anti-Fraud Policy</a>
            <a href="/support/legal-documents?section=5">Exclusive artist</a>
          </div>
          <div className="footer-col">
            <h4>Join us</h4>
            <a href="https://mail.google.com/mail/?view=cm&to=fmdigitalofficial@gmail.com&su=Career%20Inquiry" target="_blank" rel="noopener noreferrer">Career</a>
            <a href="https://mail.google.com/mail/?view=cm&to=fmdigitalofficial@gmail.com&su=Investor%20Opportunities" target="_blank" rel="noopener noreferrer">Investor Opportunities</a>
            <a href="https://mail.google.com/mail/?view=cm&to=fmdigitalofficial@gmail.com&su=Partnership%20Inquiry" target="_blank" rel="noopener noreferrer">Partnerships</a>
          </div>
          <div className="footer-partners">
            <h4>Powered By</h4>
            <img src={ASSETS.fuga} alt="FUGA" className="footer-partner-img" />
            <img src={ASSETS.umg} alt="Universal Music Group" className="footer-partner-img umg" />
          </div>
        </div>
        <div className="footer-bottom">
          <p className="footer-copy">
            <strong>© 2026 FM Digital Official Distribution.</strong> All rights reserved.<br />
            A subsidiary of Koko Advertising &amp; Marketing.
          </p>
        </div>
      </footer>

      {/* Offer popup */}
      <OfferPopup onGoToStarter={() => scrollToSel(".price-card.starter", -90)} />

      {/* Application form modal */}
      {modalPlan !== null && (
        <ApplicationModal plan={modalPlan} onClose={closeModal} onNext={goToCheckout} />
      )}

      {/* Floating WhatsApp Button */}
      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" id="whatsapp-float" aria-label="Chat on WhatsApp">
        <WhatsAppIcon />
      </a>
    </div>
  );
}
